// Browser entry — the composition root that wires the real adapters into the
// CommerceController and exposes the stable `window.JoeFlowCommerce` surface the
// monolith (index.html) calls. Bundled by Vite as an IIFE into dist/commerce/.
//
// This is the only file that imports SDKs and touches globals; it is the
// untested "dirty edge" (everything it composes is unit-tested behind ports).
// It degrades gracefully: missing Supabase config or an unavailable native store
// fall back to the offline adapters so the app never crashes on boot.

import { createClient } from '@supabase/supabase-js';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

import { CommerceController, type PaywallReason } from './bridge/commerce-controller';
import { MultiplayerController } from './bridge/multiplayer-controller';
import type {
  AuthPort,
  PurchasePort,
  BackendPort,
  LocalAiPort,
  TokenProvider,
  IdProvider,
  MultiplayerPort,
} from './bridge/ports';
import type { AiOp, AiProviderTier, CommerceState, FeatureKey, Tier } from './domain/types';
import {
  SupabaseRealtime,
  NullMultiplayer,
  type SupabaseRealtimeLike,
} from './adapters/supabase-realtime';
import {
  makeRoomId,
  type RemoteClear,
  type RemoteStrokeEnd,
  type RemoteStrokeStart,
  type RemoteStrokeUpdate,
  type RemoteUndo,
  type StrokeStartInput,
  type StrokeUpdateInput,
} from './domain/multiplayer';
import {
  mapModelToOp,
  normalizeAiResult,
  type MonolithAiRequest,
  type MonolithAiResult,
} from './bridge/ai-result';
import { getConfig, isBackendConfigured } from './config';
import { SupabaseAuth, type SupabaseAuthLike } from './adapters/supabase-auth';
import { RevenueCatPurchase, type RevenueCatLike } from './adapters/revenuecat-purchase';
import { EdgeBackend } from './adapters/edge-backend';
import { LocalAi } from './adapters/local-ai';
import { WebIdProvider } from './adapters/web-id';
import { OfflineAuth, OfflinePurchase, OfflineBackend } from './adapters/offline';
import { mountPaywall } from './ui/paywall';
import { mountLogin } from './ui/login';
import { mountOnboarding } from './ui/onboarding';
import { watermarkIfNeeded, type WatermarkCtx } from './ui/watermark';
import { aiLabelMetadata, drawAiBadge, type AiLabel } from './ui/ai-label';
import { clampExportSize, type ClampedExport } from './ui/export-resolution';
import { App } from '@capacitor/app';
import { handleAuthCallback, type PkceExchanger } from './adapters/auth-callback';

/**
 * Collaborative drawing ("Gemeinsam zeichnen") surface. Mirrors the chatpartygames
 * shared-canvas protocol over Supabase Realtime. Sync getters are safe before
 * `ready`; `join` awaits init internally. All `on*` return an unsubscribe handle.
 */
export interface JoeFlowMultiplayer {
  /** Entitlement gate (MVP: open to all). The monolith hides the feature when false. */
  canUse(): boolean;
  /** True only while subscribed to a live room. */
  isActive(): boolean;
  /** Sanitised id of the current room, or null. */
  roomId(): string | null;
  /** Stable per-client id (presence key / sender id). */
  clientId(): string;
  /** Generate a fresh shareable room id. */
  newRoomId(): string;
  /** Join a room. Resolves false when the entitlement gate blocks it. */
  join(roomId: string): Promise<boolean>;
  leave(): Promise<void>;

  sendStrokeStart(input: StrokeStartInput): void;
  sendStrokeUpdate(input: StrokeUpdateInput): void;
  sendStrokeEnd(strokeId: string): void;
  sendClear(): void;
  sendUndo(strokeId: string): void;

  onStrokeStart(cb: (s: RemoteStrokeStart) => void): () => void;
  onStrokeUpdate(cb: (s: RemoteStrokeUpdate) => void): () => void;
  onStrokeEnd(cb: (s: RemoteStrokeEnd) => void): () => void;
  onClear(cb: (c: RemoteClear) => void): () => void;
  onUndo(cb: (u: RemoteUndo) => void): () => void;
  onParticipants(cb: (count: number) => void): () => void;
}

/** The stable API the monolith consumes via `window.JoeFlowCommerce`. */
export interface JoeFlowCommerce {
  /** Resolves once init() has run (auth/entitlements/founder loaded). */
  ready: Promise<void>;

  /** Collaborative drawing ("Gemeinsam zeichnen"). */
  multiplayer: JoeFlowMultiplayer;

  // AI (replaces direct OpenRouter calls). Returns null when blocked by paywall.
  // `tier` picks the provider: 'local' (free, on-device) | 'standard' | 'premium'.
  generate(
    op: AiOp,
    imageDataUrl?: string,
    prompt?: string,
    tier?: AiProviderTier,
  ): Promise<unknown | null>;

  /**
   * Drop-in replacement for the monolith's callOpenRouterAI(): maps the
   * {model, prompt, imageDataUrl, textOnly} request to an op, runs it through
   * the credit-gated proxy, and returns the monolith's {imageUrl, textContent}
   * shape (or null when blocked by the paywall / on error).
   */
  generateForMonolith(req: MonolithAiRequest): Promise<MonolithAiResult | null>;

  // Gating
  canUse(feature: FeatureKey): boolean;
  shouldWatermark(): boolean;
  getTier(): Tier;
  requireUpgrade(reason: PaywallReason): void;

  // Export helpers (tier-aware; the monolith passes its export canvas context).
  /** Stamp the free-tier brand watermark if the current tier requires it. */
  applyExportWatermark(ctx: WatermarkCtx, width: number, height: number): boolean;
  /** Clamp an export size to the current tier's max resolution (PRD §1). */
  clampExport(width: number, height: number): ClampedExport;

  // AI Act Art. 50 transparency.
  /** Canonical machine-readable AI label for generated artefacts. */
  aiLabel(): AiLabel;
  /** Stamp the visible "KI-generiert" badge onto an AI result before export. */
  stampAiBadge(ctx: WatermarkCtx, width: number, height: number): void;

  // Onboarding (PRD §4): run `proceed` after first-run onboarding, or
  // immediately if the user has already seen it.
  onboardThen(proceed: () => void): void;

  // State / reactivity
  getState(): CommerceState;
  onChange(cb: (s: CommerceState) => void): () => void;

  // Auth (paywall / onboarding)
  signIn(provider: 'apple' | 'google'): Promise<void>;
  signInWithEmail(email: string): Promise<void>;
  signOut(): Promise<void>;

  // Purchases
  purchase(tier: Tier, period: 'monthly' | 'annual'): Promise<void>;
  restore(): Promise<void>;
  /** MOCK "Stripe" credit top-up (TEST ONLY). Grants a fixed pack; returns new balance. */
  topUp(): Promise<number>;
  refreshFounder(): Promise<void>;
}

declare global {
  interface Window {
    JoeFlowCommerce?: JoeFlowCommerce;
  }
}

interface AuthBundle {
  auth: AuthPort;
  tokens: TokenProvider;
}

/** Build the auth + token-provider pair (Supabase, else offline). */
function buildAuth(): AuthBundle {
  if (!isBackendConfigured()) {
    const offline = new OfflineAuth();
    return { auth: offline, tokens: offline };
  }
  const cfg = getConfig();
  const supabase = createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  // supabase.auth structurally satisfies the slice we use.
  const sb = new SupabaseAuth(supabase.auth as unknown as SupabaseAuthLike);

  // PKCE deep-link auth: on native, the OAuth/magic-link redirect returns via
  // com.zeichenapp.app://auth-callback?code=... — exchange it for a session.
  if (Capacitor.isNativePlatform()) {
    const exchanger = supabase.auth as unknown as PkceExchanger;
    void App.addListener('appUrlOpen', ({ url }) => {
      void handleAuthCallback(url, exchanger).then((result) => {
        if (result === 'error') console.warn('[JoeFlowCommerce] auth callback exchange failed');
        else if (result === 'auth_error')
          console.warn('[JoeFlowCommerce] auth provider returned an error (cancelled/denied)');
      });
    });
  }

  return { auth: sb, tokens: sb };
}

/** Build the purchase port. Native store via RevenueCat, else offline. */
async function buildPurchase(): Promise<PurchasePort> {
  const cfg = getConfig();
  const native = Capacitor.isNativePlatform();
  if (!native || !cfg.revenueCatApiKey) {
    // Plain web (or unconfigured): the RevenueCat Capacitor plugin has no
    // native store to talk to. Stay free/anonymous.
    return new OfflinePurchase();
  }
  try {
    await Purchases.configure({ apiKey: cfg.revenueCatApiKey });
    return new RevenueCatPurchase(Purchases as unknown as RevenueCatLike);
  } catch (err) {
    console.warn('[JoeFlowCommerce] RevenueCat configure failed; running free.', err);
    return new OfflinePurchase();
  }
}

function buildBackend(): BackendPort {
  if (!isBackendConfigured()) return new OfflineBackend();
  const cfg = getConfig();
  return new EdgeBackend({ supabaseUrl: cfg.supabaseUrl, anonKey: cfg.supabaseAnonKey });
}

/** Build the optional local (on-device) generator, or undefined when not configured. */
function buildLocalAi(): LocalAiPort | undefined {
  const cfg = getConfig();
  if (!cfg.localAiUrl) return undefined;
  return new LocalAi({ baseUrl: cfg.localAiUrl });
}

/**
 * Build the multiplayer transport. Supabase Realtime (broadcast + presence) when
 * the backend is configured; a no-op offline impl otherwise so the monolith can
 * detect that collaborative drawing is unavailable and keep working solo.
 *
 * Uses a dedicated realtime client so the websocket lifecycle is isolated from
 * auth; broadcast on public room channels needs only the anon key (no RLS).
 */
function buildMultiplayer(ids: IdProvider): MultiplayerPort {
  if (!isBackendConfigured()) return new NullMultiplayer(ids);
  const cfg = getConfig();
  const supabase = createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
    realtime: { params: { eventsPerSecond: 30 } },
  });
  return new SupabaseRealtime(supabase as unknown as SupabaseRealtimeLike, ids);
}

// Safe pre-init defaults: free tier, watermark on, no credits. The monolith may
// call sync getters before `ready` resolves; these keep it correct (conservative)
// until real state loads.
const INITIAL_STATE: CommerceState = {
  tier: 'free',
  authenticated: false,
  creditBalance: 0,
  founderActive: false,
  founderRemaining: 0,
};

// IIFE-friendly: no top-level await. We expose a synchronous façade and wire the
// async parts behind `ready`. Sync getters fall back to conservative defaults
// until the controller exists.
let controller: CommerceController | null = null;
let authRef: AuthPort | null = null;
let mpController: MultiplayerController | null = null;

const ready: Promise<void> = (async () => {
  const { auth, tokens } = buildAuth();
  const purchase = await buildPurchase();
  const backend = buildBackend();
  const localAi = buildLocalAi();
  const ids = new WebIdProvider();
  authRef = auth;

  // Accessible login overlay for the credit-costing-op gate. OAuth / magic link
  // complete via redirect, so the gate resolves false (generation aborts) and
  // the user re-taps after returning signed in. Only meaningful with a backend.
  const loginHandle =
    typeof document !== 'undefined' && isBackendConfigured()
      ? mountLogin({
          onProvider: (provider) => void auth.signInWithProvider(provider),
          onEmail: (email) => void auth.signInWithEmail(email),
        })
      : null;

  controller = new CommerceController({
    auth,
    purchase,
    backend,
    tokens,
    ids,
    ...(localAi ? { localAi } : {}),
    onRequireUpgrade: (reason) => {
      // Decouple the bridge from any UI framework: the paywall listens for this.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('joeflow:require-upgrade', { detail: { reason } }));
      }
    },
    // Login gate (only wired when a real backend exists, so offline stays
    // anonymous-first). Show the sign-in overlay; this attempt does not proceed.
    ...(loginHandle
      ? {
          onRequireLogin: async () => {
            loginHandle.show();
            return false;
          },
        }
      : {}),
  });

  await controller.init();

  // Collaborative drawing transport. The entitlement gate reads through the
  // commerce controller's canUse('multiplayer') so it stays a single source of
  // truth (MVP: open). Reuses the same id provider for a stable client id.
  mpController = new MultiplayerController({
    transport: buildMultiplayer(ids),
    canUse: () => controller?.canUse('multiplayer') ?? true,
  });

  // Mount the accessible paywall. It listens for the `joeflow:require-upgrade`
  // event the controller dispatches and drives purchases back through it.
  if (typeof document !== 'undefined') {
    mountPaywall({
      getState: () => {
        const s = controller?.getState();
        return {
          founderActive: s?.founderActive ?? false,
          founderRemaining: s?.founderRemaining ?? 0,
        };
      },
      onSelectTier: (tier, period) => {
        void controller?.purchase(tier, period);
      },
      onRestore: () => {
        void controller?.restore();
      },
      onTopUp: () => controller?.topUp() ?? Promise.reject(new Error('not_ready')),
    });
  }
})().catch((err) => {
  console.error('[JoeFlowCommerce] init failed; UI reflects free/anonymous.', err);
});

async function whenAuth(): Promise<AuthPort> {
  await ready;
  if (!authRef) throw new Error('joeflow_commerce_not_ready');
  return authRef;
}
async function whenController(): Promise<CommerceController> {
  await ready;
  if (!controller) throw new Error('joeflow_commerce_not_ready');
  return controller;
}
async function whenMultiplayer(): Promise<MultiplayerController> {
  await ready;
  if (!mpController) throw new Error('joeflow_commerce_not_ready');
  return mpController;
}
/**
 * Subscribe to a multiplayer stream even if called before `ready`: register now
 * when the controller exists, else defer to after init. The returned handle
 * unsubscribes in both cases.
 */
function deferMpSub(register: (c: MultiplayerController) => () => void): () => void {
  if (mpController) return register(mpController);
  let dispose = () => {};
  let cancelled = false;
  void ready.then(() => {
    if (!cancelled && mpController) dispose = register(mpController);
  });
  return () => {
    cancelled = true;
    dispose();
  };
}

const multiplayer: JoeFlowMultiplayer = {
  canUse: () => mpController?.canUse() ?? true, // MVP open even before init
  isActive: () => mpController?.isActive() ?? false,
  roomId: () => mpController?.roomId() ?? null,
  clientId: () => mpController?.clientId() ?? '',
  newRoomId: () => makeRoomId(Math.random),
  join: async (roomId) => (await whenMultiplayer()).join(roomId),
  leave: async () => {
    if (mpController) await mpController.leave();
  },
  sendStrokeStart: (input) => mpController?.sendStrokeStart(input),
  sendStrokeUpdate: (input) => mpController?.sendStrokeUpdate(input),
  sendStrokeEnd: (strokeId) => mpController?.sendStrokeEnd(strokeId),
  sendClear: () => mpController?.sendClear(),
  sendUndo: (strokeId) => mpController?.sendUndo(strokeId),
  onStrokeStart: (cb) => deferMpSub((c) => c.onStrokeStart(cb)),
  onStrokeUpdate: (cb) => deferMpSub((c) => c.onStrokeUpdate(cb)),
  onStrokeEnd: (cb) => deferMpSub((c) => c.onStrokeEnd(cb)),
  onClear: (cb) => deferMpSub((c) => c.onClear(cb)),
  onUndo: (cb) => deferMpSub((c) => c.onUndo(cb)),
  onParticipants: (cb) => deferMpSub((c) => c.onParticipants(cb)),
};

const instance: JoeFlowCommerce = {
  ready,
  multiplayer,
  generate: async (op, imageDataUrl, prompt, tier) =>
    (await whenController()).generate(op, imageDataUrl, prompt, tier ?? 'standard'),
  generateForMonolith: async (req) => {
    const op = mapModelToOp(req);
    const res = await (
      await whenController()
    ).generate(op, req.imageDataUrl, req.prompt, req.quality ?? 'standard');
    if (res == null) return null; // paywall opened (out of credits) or error
    return normalizeAiResult(op, (res as { result?: unknown }).result);
  },
  canUse: (feature) => controller?.canUse(feature) ?? false,
  shouldWatermark: () => controller?.shouldWatermark() ?? true,
  getTier: () => controller?.getTier() ?? 'free',
  requireUpgrade: (reason) => controller?.requireUpgrade(reason),
  applyExportWatermark: (ctx, width, height) =>
    watermarkIfNeeded(ctx, width, height, controller?.getTier() ?? 'free'),
  clampExport: (width, height) => clampExportSize(width, height, controller?.getTier() ?? 'free'),
  aiLabel: () => aiLabelMetadata(),
  stampAiBadge: (ctx, width, height) => drawAiBadge(ctx, width, height),
  onboardThen: (proceed) => {
    if (typeof document === 'undefined') return proceed();
    const ob = mountOnboarding({ onComplete: proceed });
    if (!ob.maybeShow()) proceed(); // already seen → proceed immediately
  },
  getState: () => controller?.getState() ?? { ...INITIAL_STATE },
  onChange: (cb) => {
    if (controller) return controller.onChange(cb);
    let dispose = () => {};
    let cancelled = false;
    void ready.then(() => {
      if (!cancelled && controller) dispose = controller.onChange(cb);
    });
    return () => {
      cancelled = true;
      dispose();
    };
  },
  signIn: async (provider) => {
    await (await whenAuth()).signInWithProvider(provider);
  },
  signInWithEmail: async (email) => (await whenAuth()).signInWithEmail(email),
  signOut: async () => (await whenAuth()).signOut(),
  purchase: async (tier, period) => (await whenController()).purchase(tier, period),
  restore: async () => (await whenController()).restore(),
  topUp: async () => (await whenController()).topUp(),
  refreshFounder: async () => (await whenController()).refreshFounder(),
};

if (typeof window !== 'undefined') {
  window.JoeFlowCommerce = instance;
}

export default instance;
